import moment from 'moment';

/**
 * Day dividers for the history panel.
 *
 * A divider reads as distance from now, because that is the question being asked of a
 * history — "was this recent?" — and a bare date makes the reader do the subtraction.
 * The date is kept beside the relative word so the divider is still an address you can
 * cite; "3 weeks ago" on its own is not.
 */

// the coarsest unit that still has a whole one in it, so the label reads at the scale the
// reader is actually thinking in
const SCALES = Object.freeze([
  { unit: 'years', one: 'Last year', many: 'years ago' },
  { unit: 'months', one: 'Last month', many: 'months ago' },
  { unit: 'weeks', one: 'Last week', many: 'weeks ago' }
]);

const distanceLabel = (day, today) => {
  for (const { unit, one, many } of SCALES) {
    // moment truncates, so this means "at least n whole units ago"
    const count = today.diff(day, unit);
    if (count >= 1) {
      return count === 1 ? one : `${count} ${many}`;
    }
  }

  const days = today.diff(day, 'days');
  if (days === 0) {
    return 'Today';
  }
  if (days === 1) {
    return 'Yesterday';
  }

  return day.format('dddd');
};

/**
 * @param {*} value anything moment can parse
 * @returns {string} e.g. "Today, Aug 23", "Wednesday, Aug 19", "2 weeks ago, Aug 9"
 */
export const dayLabel = (value) => {
  const day = moment(value).startOf('day');
  const today = moment().startOf('day');

  // a future stamp has no sensible distance, so it is shown as the plain date
  if (day.isAfter(today)) {
    return day.format('ll');
  }

  // the year is dropped while it is still the current year — repeating it on every
  // divider states nothing
  const date = day.isSame(today, 'year') ? day.format('MMM D') : day.format('ll');

  return `${distanceLabel(day, today)}, ${date}`;
};

export default dayLabel;
